import { ExternalLink } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ThumbnailWithFallback } from "@/components/shared/thumbnail-with-fallback"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import type { Collab } from "@/types/collab"
import type { PickerProps, Submission } from "@/types/submissions"
import { SUBMISSION_CAP } from "@/types/submissions"

import { DeleteSubmissionConfirmDialog } from "./delete-submission-confirm-dialog"
import { SubmissionStateChip } from "./submission-state-chip"
import { TikTokPostPicker } from "./tiktok-post-picker"

/**
 * OKS-35 — Creator-side submissions panel (UX Spec §2).
 *
 * Renders 5 states:
 *   - empty (0 submissions, slot free)
 *   - loading (loading prop true)
 *   - error (errorCode set)
 *   - success (1–3 rows + add another)
 *   - edge: at-cap, locked-at-3-approved, gate-not-yet-eligible,
 *     reconnect-tiktok
 *
 * Submission gate: render only when `collab.state` ∈ {accepted,
 * delivered, posted}. The parent page filters; this component
 * defends with `gateNotEligible` for completeness.
 *
 * Approved-row delete is hidden (state-locked terminal).
 *
 * OKS-599 — the header names the target mission (cover thumbnail + demoted
 * "Submit your post" eyebrow + prominent, truncated mission name) so that
 * when multiple panels stack on the creator missions screen, each panel's
 * target mission is unambiguous (no wrong-mission submissions). Display-only,
 * rendered in all 5 states; reads `collab.mission` already on props.
 */

const ELIGIBLE_STATES: Collab["state"][] = ["accepted", "delivered", "posted"]

interface Props {
  collab: Collab
  submissions: Submission[]
  picker?: PickerProps
  loading?: boolean
  errorCode?: "tiktok_auth_revoked" | "missing_social_account" | null
  /**
   * OKS-445 — controlled picker-open state. When provided, the parent owns
   * the open/close lifecycle (so the sibling card's `submit` CTA can open
   * this panel's picker). When omitted, the panel falls back to its own
   * internal state (back-compat for any consumer that drives the panel's own
   * pick button standalone).
   */
  pickerOpen?: boolean
  onPickerOpenChange?: (open: boolean) => void
  /**
   * OKS-445 — single-affordance rule (UX Spec §2). When true, the panel's
   * empty-state pick button is suppressed at ZERO submissions (the sibling
   * card CTA is the sole picker entry there). The empty helper, the
   * submitted-rows list, the "Add another" CTA (counts 1–2) and the at-cap
   * helper are all unaffected.
   */
  suppressEmptyStatePickCta?: boolean
  /**
   * OKS-481 (§09) — post-submit posting streak (page prop, forwarded to the
   * picker's in-dialog Submitted success state). Null/absent → no chip.
   */
  streak?: { current_days: number } | null
}

export function SubmissionsPanel({
  collab,
  submissions,
  picker,
  loading = false,
  errorCode = null,
  pickerOpen: controlledPickerOpen,
  onPickerOpenChange,
  suppressEmptyStatePickCta = false,
  streak = null,
}: Props) {
  const { t } = useTranslation()
  const [internalPickerOpen, setInternalPickerOpen] = useState(false)
  const isControlled = controlledPickerOpen !== undefined
  const pickerOpen = isControlled ? controlledPickerOpen : internalPickerOpen
  const setPickerOpen = (open: boolean) => {
    if (!isControlled) setInternalPickerOpen(open)
    onPickerOpenChange?.(open)
  }

  const gateNotEligible = !ELIGIBLE_STATES.includes(collab.state)
  const counted = submissions.length
  const approvedCount = submissions.filter((s) => s.state === "approved").length
  const atCap = counted >= SUBMISSION_CAP
  const lockedAtCap = atCap && approvedCount >= SUBMISSION_CAP
  const authRevoked =
    errorCode === "tiktok_auth_revoked" ||
    errorCode === "missing_social_account"
  // OKS-445 — at zero submissions the card CTA is the single picker entry;
  // suppress the panel's redundant empty-state pick button. "Add another"
  // (counts 1–2) is unaffected. The footer is omitted entirely when it would
  // otherwise hold only the suppressed button.
  const hideEmptyStatePick = suppressEmptyStatePickCta && counted === 0
  const showPickButton = !atCap && !hideEmptyStatePick
  const showFooter =
    !gateNotEligible && !authRevoked && (atCap || showPickButton)

  return (
    <Card data-testid={`submissions-panel-${collab.id}`}>
      {/* OKS-599 — mission-context header. Disambiguates which mission the
          panel submits to when multiple panels stack on one screen. Reads
          `collab.mission` (already on props via Collabs::CardSerializer) — no
          cross-collab lookup, no new wire fields. Display-only: not a link,
          not tappable (the submit CTA stays the sole action). Rendered in all
          5 panel states so every stacked panel is identifiable by mission.

          OKS-607 — `min-w-0` on the header rows: `CardHeader` is a grid whose
          content track defaults to `min-width: auto`, so this flex row (a grid
          item) would not shrink below its min-content and the `flex-1 min-w-0`
          text column never got squeezed — leaving `truncate` disengaged and
          pushing 6px of document overflow at 320px. `min-w-0` here lets the
          grid track shrink the row so the mission name truncates in-bounds. */}
      <CardHeader>
        {loading ? (
          <div
            className="flex min-w-0 items-center gap-3"
            data-testid={`submissions-panel-mission-header-skeleton-${collab.id}`}
          >
            <Skeleton className="size-11 flex-shrink-0 rounded" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        ) : (
          <div
            className="flex min-w-0 items-center gap-3"
            data-testid={`submissions-panel-mission-header-${collab.id}`}
          >
            {/* Decorative cover (alt=""): the mission-name text carries the
                identity, never the image. Neutral muted-square fallback on a
                null OR broken URL (never a broken-image glyph); the fixed-size
                slot keeps stacked panel headers aligned with no layout shift. */}
            <div
              className="size-11 flex-shrink-0"
              data-testid={`submissions-panel-mission-cover-${collab.id}`}
            >
              <ThumbnailWithFallback
                src={collab.mission.cover_image_url}
                alt=""
                loading="lazy"
                className="size-11 rounded object-cover"
                fallback={
                  <div
                    aria-hidden="true"
                    data-testid={`submissions-panel-mission-cover-fallback-${collab.id}`}
                    className="bg-muted size-11 rounded"
                  />
                }
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-muted-foreground text-xs">
                {t("submissions.creator.empty_title")}
              </span>
              {/* Truncate long titles to a single line; expose the full string
                  via `title` for hover + assistive tech. */}
              <CardTitle
                className="truncate text-base"
                title={collab.mission.title}
                data-testid={`submissions-panel-mission-name-${collab.id}`}
              >
                {collab.mission.title}
              </CardTitle>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {gateNotEligible && (
          <p
            className="text-muted-foreground text-sm"
            data-testid={`submissions-panel-gate-${collab.id}`}
          >
            {t("submissions.creator.gate_not_yet_eligible")}
          </p>
        )}

        {!gateNotEligible && authRevoked && (
          <p
            className="text-destructive text-sm"
            data-testid={`submissions-panel-reconnect-${collab.id}`}
          >
            {t("submissions.creator.reconnect_tiktok")}
          </p>
        )}

        {!gateNotEligible && !authRevoked && counted === 0 && !loading && (
          <p
            className="text-muted-foreground text-sm"
            data-testid={`submissions-panel-empty-${collab.id}`}
          >
            {t("submissions.creator.empty_helper")}
          </p>
        )}

        {!gateNotEligible && submissions.length > 0 && (
          <ul
            className="flex flex-col gap-2"
            data-testid={`submissions-panel-list-${collab.id}`}
          >
            {submissions.map((submission) => (
              <SubmissionRow key={submission.id} submission={submission} />
            ))}
          </ul>
        )}
      </CardContent>

      {showFooter && (
        <CardFooter className="flex flex-col items-stretch gap-2">
          {atCap ? (
            <p
              className="text-muted-foreground text-sm"
              data-testid={`submissions-panel-at-cap-helper-${collab.id}`}
            >
              {lockedAtCap
                ? t("submissions.creator.at_cap_locked_helper")
                : t("submissions.creator.at_cap_helper")}
            </p>
          ) : (
            <>
              <Button
                type="button"
                variant="default"
                onClick={() => setPickerOpen(true)}
                data-testid={`submissions-panel-pick-cta-${collab.id}`}
              >
                {counted === 0
                  ? t("submissions.creator.pick_cta")
                  : t("submissions.creator.add_another_cta", {
                      count: counted,
                      cap: SUBMISSION_CAP,
                    })}
              </Button>
              {/* OKS-640 — clarify that the first approval already completes the
                  collab; extra posts are optional bonus. Shown only alongside
                  the "Add another" CTA (counts 1–2), not the empty-state pick. */}
              {counted >= 1 && (
                <p
                  className="text-muted-foreground text-sm"
                  data-testid={`submissions-panel-completion-bonus-helper-${collab.id}`}
                >
                  {t("submissions.creator.completion_bonus_helper")}
                </p>
              )}
            </>
          )}
        </CardFooter>
      )}

      <TikTokPostPicker
        collabId={collab.id}
        picker={picker}
        missionTitle={collab.mission.title}
        streak={streak}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
      />
    </Card>
  )
}

function SubmissionRow({ submission }: { submission: Submission }) {
  const { t } = useTranslation()
  const isApproved = submission.state === "approved"
  const isFlagged = submission.state === "flagged"
  const [deleteOpen, setDeleteOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  return (
    <li
      className="flex items-start gap-3 rounded-md border p-3"
      data-testid={`submission-row-${submission.id}`}
    >
      {/* OKS-506 — fallback fires on a null cover OR when the snapshot cover
          (signed CDN URL, ~6h TTL) 404s after expiry; both render the muted
          square instead of a broken-image glyph. */}
      <ThumbnailWithFallback
        src={submission.tiktok_video_thumbnail_url}
        alt=""
        loading="lazy"
        className="size-[76px] flex-shrink-0 rounded object-cover"
        fallback={
          <div
            aria-hidden="true"
            data-testid="submission-thumb-fallback"
            className="bg-muted size-[76px] flex-shrink-0 rounded"
          />
        }
      />

      <div className="flex flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <SubmissionStateChip state={submission.state} />
          <a
            href={submission.tiktok_video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground text-sm hover:underline"
            aria-label={t("submissions.creator.tiktok_open_aria")}
            data-testid={`submission-permalink-${submission.id}`}
          >
            <ExternalLink aria-hidden="true" className="inline size-3.5" />
          </a>
        </div>

        {submission.caption_excerpt && (
          <p className="line-clamp-1 text-sm">{submission.caption_excerpt}</p>
        )}

        {isFlagged && submission.flag_note && (
          <Collapsible>
            <CollapsibleTrigger
              render={
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="text-muted-foreground h-auto justify-start p-0 text-xs"
                  data-testid={`submission-flag-note-trigger-${submission.id}`}
                />
              }
            >
              {t("submissions.creator.flag_note_label")}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <blockquote
                className="mt-1 border-l-2 border-amber-300 pl-2 text-xs"
                aria-label={t("submissions.creator.flag_note_label")}
                data-testid={`submission-flag-note-${submission.id}`}
              >
                {submission.flag_note}
              </blockquote>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {!isApproved && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={t("submissions.creator.delete_kebab")}
                data-testid={`submission-row-actions-${submission.id}`}
              >
                ⋯
              </Button>
            }
          />
          <DropdownMenuContent align="end" finalFocus={finalFocus}>
            <DropdownMenuItem
              onClick={() => {
                yieldFocusToDialog()
                setDeleteOpen(true)
              }}
              data-testid={`delete-submission-trigger-${submission.id}`}
            >
              {t("submissions.creator.delete_kebab")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {!isApproved && (
        <DeleteSubmissionConfirmDialog
          submission={submission}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      )}
    </li>
  )
}
